<template>
  <mailbox-verification-layout :name="mailboxName" show-cancel-option show-start-over-option>
    <el-card>
      <entity-settings-loading v-if="isLoading" />
      <div v-else class="mailbox-verification-auto-forwarding">
        <div v-if="emailProvider===null" class="mailbox-verification-auto-forwarding__provider-select">
          <h3>Choose the email provider for {{ forwardingFromAddress }}</h3>
          <p>We'll provide instructions on how to set up your auto-forwarding rule.</p>
          <div class="provider-grid">
            <div @click="emailProvider='GOOGLE'">
              <img src="./logos/gmail-logo.svg">
              <p class="small">Google Workspace (Gmail)</p>
            </div>
            <div @click="emailProvider='MICROSOFT'">
              <img src="./logos/microsoft-logo.svg">
              <p class="small">Microsoft 365, Exchange Server, OWA</p>
            </div>
            <div @click="emailProvider='ZOHO'">
              <img src="./logos/zoho-logo.svg">
              <p class="small">Zoho Mail</p>
            </div>
            <div @click="emailProvider='GODADDY'">
              <img src="./logos/godaddy-logo.svg">
              <p class="small">Go Daddy Email Hosting</p>
            </div>
            <div @click="emailProvider='RACKSPACE'">
              <img src="./logos/rackspace-logo.png">
              <p class="small">Rackspace Email</p>
            </div>
            <div @click="emailProvider='YAHOO'">
              <img src="./logos/yahoo-logo.svg">
              <p class="small">Yahoo! Small Business</p>
            </div>
            <div @click="emailProvider='CPANEL'">
              <img src="./logos/cpanel-logo.svg">
              <p class="small">cPanel Email</p>
            </div>
            <div @click="emailProvider='OTHER'">
              <i class="ddi ddi-email" />
              <p class="small">Another email provider</p>
            </div>
          </div>
        </div>
        <!-- GOOGLE instructions -->
        <div v-if="emailProvider==='GOOGLE'" class="instruction-set">
          <mailbox-verification-instructions-google-business 
            :incoming-email-address="autoForwardingSettings.incomingEmailAddress"
            :forwarding-from-address="forwardingFromAddress"
            @completion-clicked="goToAutoForwardingTest"
          />
        </div>
        <!-- Microsoft OWA instructions -->
        <div v-if="emailProvider==='MICROSOFT'" class="instruction-set">
          <mailbox-verification-instructions-microsoft
            :incoming-email-address="autoForwardingSettings.incomingEmailAddress"
            :forwarding-from-address="forwardingFromAddress"
            @completion-clicked="goToAutoForwardingTest"
          />
        </div>
        <!-- Rackspace instructions -->
        <div v-if="emailProvider==='RACKSPACE'" class="instruction-set">
          <mailbox-verification-instructions-rackspace
            :incoming-email-address="autoForwardingSettings.incomingEmailAddress"
            :forwarding-from-address="forwardingFromAddress"
            @completion-clicked="goToAutoForwardingTest"
          />
        </div>
        <!-- Zoho instructions -->
        <div v-if="emailProvider==='ZOHO'" class="instruction-set">
          <mailbox-verification-instructions-zoho
            :incoming-email-address="autoForwardingSettings.incomingEmailAddress"
            :forwarding-from-address="forwardingFromAddress"
            @completion-clicked="goToAutoForwardingTest"
          />
        </div>
        <!-- Yahoo! instructions -->
        <div v-if="emailProvider==='YAHOO'" class="instruction-set">
          <mailbox-verification-instructions-yahoo
            :incoming-email-address="autoForwardingSettings.incomingEmailAddress"
            :forwarding-from-address="forwardingFromAddress"
            @completion-clicked="goToAutoForwardingTest"
          />
        </div>
        <!-- GoDaddy instructions -->
        <div v-if="emailProvider==='GODADDY'" class="instruction-set">
          <mailbox-verification-instructions-go-daddy
            :incoming-email-address="autoForwardingSettings.incomingEmailAddress"
            :forwarding-from-address="forwardingFromAddress"
            @completion-clicked="goToAutoForwardingTest"
          />
        </div>
        <!-- CPanel instructions -->
        <div v-if="emailProvider==='CPANEL'" class="instruction-set">
          <mailbox-verification-instructions-cpanel
            :incoming-email-address="autoForwardingSettings.incomingEmailAddress"
            :forwarding-from-address="forwardingFromAddress"
            @completion-clicked="goToAutoForwardingTest"
          />
        </div>
        <!-- CPanel instructions -->
        <div v-if="emailProvider==='OTHER'" class="instruction-set">
          <mailbox-verification-instructions-no-provider
            :incoming-email-address="autoForwardingSettings.incomingEmailAddress"
            :forwarding-from-address="forwardingFromAddress"
            @completion-clicked="goToAutoForwardingTest"
          />
        </div>
      </div>
    </el-card>
  </mailbox-verification-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import MailboxVerificationLayout from '@/layouts/mailbox-verification-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import MailboxVerificationInstructionsGoogleBusiness from './components/mailbox-verification-instructions-google-business.vue'
import MailboxVerificationInstructionsMicrosoft from './components/mailbox-verification-instructions-microsoft.vue'
import MailboxVerificationInstructionsRackspace from './components/mailbox-verification-instructions-rackspace.vue'
import MailboxVerificationInstructionsZoho from './components/mailbox-verification-instructions-zoho.vue'
import MailboxVerificationInstructionsYahoo from './components/mailbox-verification-instructions-yahoo.vue'
import MailboxVerificationInstructionsGoDaddy from './components/mailbox-verification-instructions-go-daddy.vue'
import MailboxVerificationInstructionsCpanel from './components/mailbox-verification-instructions-cpanel.vue'
import MailboxVerificationInstructionsNoProvider from './components/mailbox-verification-instructions-no-provider.vue'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'
import LocalStore from '@/helpers/localstore'

/* Data types */
import { IMailboxesOutputAutoForwarding } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default defineComponent({

  components: {
    MailboxVerificationLayout,
    EntitySettingsLoading,
    MailboxVerificationInstructionsGoogleBusiness,
    MailboxVerificationInstructionsMicrosoft,
    MailboxVerificationInstructionsRackspace,
    MailboxVerificationInstructionsZoho,
    MailboxVerificationInstructionsYahoo,
    MailboxVerificationInstructionsGoDaddy,
    MailboxVerificationInstructionsCpanel,
    MailboxVerificationInstructionsNoProvider,
  },

  data() {
    return {
      RouteAssistant,
      LocalStore,
      mailboxName: '',
      isLoading: true,
      isSubmitting: false,
      showVerificationPopup: false,
      emailProvider: null as any,
      autoForwardingSettings: {} as IMailboxesOutputAutoForwarding,
    }
  },
  head: {
    title: 'Verify Auto-Forwarding Address',
  },

  computed: {
    accountID(): number {
      return parseInt(this.$route.params.accountId, 10)
    },
    mailboxID(): number {
      return Number(this.$route.params.id)
    },
    title(): string {
      return `Auto-forward from ${this.forwardingFromAddress}`
    },
    forwardingFromAddress(): string {
      return LocalStore.mailboxForwardingAddressCandidate
    },
  },

  async created() {
    this.isLoading = true
    await this.getAutoForwardingInfo()
    if (!this.forwardingFromAddress) {
      RouteAssistant.goToMailboxVerificationIntroRoute(
        this.$router,
        this.accountID,
        this.mailboxID,
      )
    }
    this.isLoading = false
  },

  methods: {
    async getAutoForwardingInfo() {
      try {
        this.autoForwardingSettings = await MailboxesAPI.get_AutoForwarding(
          this.mailboxID,
        )
        this.mailboxName = this.autoForwardingSettings.name
      } catch (err) {
        this.$error.emit(err)
      }
    },
    async goToAutoForwardingTest() {
      RouteAssistant.goToMailboxVerificationAutoForwardingTestingRoute(
        this.$router,
        this.accountID,
        this.mailboxID,
      )
    },
  },
})
</script>

<style lang="scss">
.mailbox-verification-auto-forwarding {
  // Don't remove. Referenced in child components.
  .bouncy {
    animation-name: dd-bounce;
    animation-duration: 1s;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
    animation-direction: alternate;
  }

  /* For Gmail and Zoho */
  .el-carousel {
    &__arrow {
      background-color: $mailboxes--theme-color;
      outline: none;

      > i {
        font-size: 24px;
        padding-top: 2px;
      }
    }

    &__item {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      overflow: visible;
      padding: 0 40px;

      .prev-step-button {
        position: absolute;
        top: 0;
      }

      > img {
        width: 60%;
        border-radius: 8px;
        border: solid 3px $color--gray-15;
      }

      > h3,
      > p {
        text-align: center;
      }

      > p.small {
        color: var(--color-text-third);
      }
    }

    &__container {
      height: 480px;
    }
  }

  &__provider-select {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .progress-bar {
    position: absolute;
    top: -20px;
    width: 100%;
  }

  .provider-grid {
    display: grid;
    grid: auto-flow / 1fr 1fr;
    width: calc(100% + 8px);
    margin-left: -4px;
    padding-top: 20px;

    div {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      border: $border--solid-light;
      padding: 20px;
      border-radius: 8px;
      margin: 4px;
      cursor: pointer;

      &:hover {
        border-color: $color--blue;
      }

      > img {
        height: 28px;
      }

      > i {
        font-size: 32px;
      }

      > p {
        margin-top: 10px;
        text-align: center;
      }
    }
  }

  .instruction-set {
    padding: 10px 0;

    > * {
      position: relative;
    }
  }

  /* Absolute bottom centering of an element */
  .bottom-centered {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    bottom: 0;
  }

  .provider-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 30px;

    img {
      height: 28px;
    }

    p {
      @include lettering--p-sm;
      background-color: var(--color-background-secondary);
      padding: 8px 20px;
      border-radius: 20px;
    }
  }

  .step-wrapper {
    align-items: flex-start;
  }

  .step {
    display: flex;
    padding-top: 20px;

    &:last-of-type {
      padding-bottom: 20px;
    }

    > div {
      width: 100%;
    }

    /* Step number */
    > span {
      border-radius: 50%;
      border: solid 1px $mailboxes--theme-color;
      color: $mailboxes--theme-color;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 10px;
    }
  }
}
</style>
